<template>
    <div class="contractor-documents-page">
        <app-sub-title text="Gestión de Documentos" />
        <p class="text-muted">Seleccione un cliente y suba la documentación requerida.</p>

        <b-form-group label="Cliente">
            <b-form-select
                v-model="selectedClientId"
                :options="clientOptions"
                value-field="id"
                text-field="name"
                @change="onClientChange"
            >
                <template #first>
                    <b-form-select-option :value="null" disabled>-- Seleccione un cliente --</b-form-select-option>
                </template>
            </b-form-select>
        </b-form-group>

        <template v-if="selectedClientId">
            <b-card v-if="documentTypes.length === 0 && !loadingTypes" class="mb-3">
                <p class="mb-0 text-muted">Este cliente no tiene tipos de documento configurados.</p>
            </b-card>

            <div v-else>
                <b-card v-for="type in documentTypes" :key="type.id" class="mb-3">
                    <template #header>
                        <span>{{ type.name }}</span>
                        <span v-if="type.expiry_date" class="badge badge-info ml-2">Con fecha de vencimiento</span>
                    </template>
                    <div v-if="getMyDocForType(type.id)" class="mb-2">
                        <span class="mr-2">Documento subido</span>
                        <b-badge :variant="docStatusVariant(getMyDocForType(type.id).status)">
                            {{ docStatusLabel(getMyDocForType(type.id).status) }}
                        </b-badge>
                        <span v-if="getMyDocForType(type.id).expiry_date" class="ml-2 text-muted">
                            Vence: {{ formatDate(getMyDocForType(type.id).expiry_date) }}
                        </span>
                        <b-button size="sm" variant="link" class="p-0 ml-2" @click="downloadDoc(getMyDocForType(type.id).id)">
                            <i class="fa fa-download"></i> Descargar
                        </b-button>
                        <div v-if="getMyDocForType(type.id).status === 'rejected' && getMyDocForType(type.id).rejection_reason" class="mt-2 p-2 bg-light rounded small text-danger">
                            <strong>Motivo del rechazo:</strong> {{ getMyDocForType(type.id).rejection_reason }}
                        </div>
                    </div>
                    <b-form @submit.prevent="uploadForType(type)">
                        <b-form-group label="Subir / Reemplazar archivo" label-size="sm">
                            <b-form-file
                                v-model="uploadFiles[type.id]"
                                accept=".pdf,.jpg,.jpeg,.png"
                                size="sm"
                                :state="null"
                            />
                        </b-form-group>
                        <b-form-group v-if="type.expiry_date" label="Fecha de vencimiento*" label-size="sm" :invalid-feedback="type.expiry_date && !uploadExpiry[type.id] && uploadExpiryTouched[type.id] ? 'Requerido' : ''">
                            <b-form-input
                                v-model="uploadExpiry[type.id]"
                                type="date"
                                size="sm"
                                :state="type.expiry_date && uploadExpiryTouched[type.id] ? (uploadExpiry[type.id] ? true : false) : null"
                                required
                            />
                        </b-form-group>
                        <b-button type="submit" size="sm" variant="primary" :disabled="!canSubmit(type) || uploading === type.id">
                            <span v-if="uploading === type.id"><i class="fa fa-spinner fa-spin"></i> Subiendo...</span>
                            <span v-else>Subir</span>
                        </b-button>
                    </b-form>
                </b-card>
            </div>
        </template>
    </div>
</template>
<script>
import contractorDocumentsRepository from '../../../repositories/contractor-documents.repository';

export default {
    name: 'ContractorDocumentsPage',
    data() {
        return {
            clients: [],
            selectedClientId: null,
            documentTypes: [],
            myDocuments: [],
            loadingTypes: false,
            uploadFiles: {},
            uploadExpiry: {},
            uploadExpiryTouched: {},
            uploading: null
        };
    },
    computed: {
        clientOptions() {
            return this.clients.map(c => ({ id: c.id, name: c.name }));
        }
    },
    created() {
        this.loadClients();
    },
    methods: {
        loadClients() {
            contractorDocumentsRepository.myClients()
                .then(res => {
                    this.clients = (res.data && res.data) || [];
                })
                .catch(() => {
                    toastr.error('Error al cargar los clientes');
                });
        },
        onClientChange() {
            this.documentTypes = [];
            this.myDocuments = [];
            this.uploadFiles = {};
            this.uploadExpiry = {};
            this.uploadExpiryTouched = {};
            if (!this.selectedClientId) return;
            this.loadingTypes = true;
            Promise.all([
                contractorDocumentsRepository.documentTypes(this.selectedClientId),
                contractorDocumentsRepository.myDocuments(this.selectedClientId)
            ]).then(([typesRes, docsRes]) => {
                this.documentTypes = (typesRes.data && typesRes.data) || [];
                this.myDocuments = (docsRes.data && docsRes.data) || [];
            }).catch(() => {
                toastr.error('Error al cargar la información del cliente');
            }).finally(() => {
                this.loadingTypes = false;
            });
        },
        getMyDocForType(typeId) {
            return this.myDocuments.find(d => d.contractor_document_type_id === typeId);
        },
        formatDate(value) {
            if (!value) return '';
            const d = new Date(value);
            return d.toLocaleDateString('es-CO');
        },
        docStatusLabel(status) {
            const map = {
                pending: 'Pendiente',
                pending_approval: 'Pendiente de aprobación',
                approved: 'Aprobado',
                rejected: 'Rechazado',
            };
            return map[status] || status || 'Pendiente';
        },
        docStatusVariant(status) {
            const map = {
                pending: 'secondary',
                pending_approval: 'warning',
                approved: 'success',
                rejected: 'danger',
            };
            return map[status] || 'secondary';
        },
        canSubmit(type) {
            if (!this.uploadFiles[type.id]) return false;
            if (type.expiry_date && !this.uploadExpiry[type.id]) return false;
            return true;
        },
        uploadForType(type) {
            const file = this.uploadFiles[type.id];
            if (!file) return;
            if (type.expiry_date && !this.uploadExpiry[type.id]) {
                this.$set(this.uploadExpiryTouched, type.id, true);
                toastr.warning('La fecha de vencimiento es obligatoria para este tipo de documento.');
                return;
            }
            this.$set(this.uploadExpiryTouched, type.id, false);
            const formData = new FormData();
            formData.append('client_id', this.selectedClientId);
            formData.append('contractor_document_type_id', type.id);
            formData.append('file', file);
            if (type.expiry_date && this.uploadExpiry[type.id]) {
                formData.append('expiry_date', this.uploadExpiry[type.id]);
            }
            this.uploading = type.id;
            contractorDocumentsRepository.upload(formData)
                .then(res => {
                    if (res.data && res.data.status) {
                        toastr.success(res.data.msg || 'Documento subido correctamente');
                        this.uploadFiles[type.id] = null;
                        this.uploadExpiry[type.id] = null;
                        const doc = res.data.document;
                        const idx = this.myDocuments.findIndex(d => d.contractor_document_type_id === type.id);
                        if (idx >= 0) this.myDocuments.splice(idx, 1, doc);
                        else this.myDocuments.push(doc);
                    } else {
                        toastr.error(res.data && res.data.msg ? res.data.msg : 'Error al subir');
                    }
                })
                .catch(err => {
                    const msg = (err.response && err.response.data && err.response.data.msg) || 'Error al subir el documento';
                    toastr.error(msg);
                })
                .finally(() => {
                    this.uploading = null;
                });
        },
        downloadDoc(id) {
            contractorDocumentsRepository.download(id)
                .then(res => {
                    const blob = res.data;
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `documento_${id}.pdf`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch(() => {
                    toastr.error('Error al descargar');
                });
        }
    }
};
</script>
